import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClipboardCheck, FaClipboardList, FaTruck, FaWallet } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { publisherSelfAPI } from '../../utils/api';
import { DashboardChartGrid, KpiGrid, PortalHero } from '../../components_shared/PortalDashboard';

const normalize = row => ({
  ...row,
  orderNumber: row.orderNumber || row.order_number,
  orderDate: row.orderDate || row.order_date,
  totalQuantity: row.totalQuantity ?? row.total_quantity ?? 0,
  totalPrice: row.totalPrice ?? row.total_price ?? 0,
  package: row.package || row.Package,
});

export default function PublisherDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [summary, workOrders] = await Promise.all([
          publisherSelfAPI.getSummary(),
          publisherSelfAPI.getWorkOrders(),
        ]);
        setStats(summary.data || {});
        setRecent((workOrders.data?.content || workOrders.data || []).map(normalize).slice(0, 6));
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Error', text: e.message });
      }
    })();
  }, []);

  const cards = useMemo(() => [
    { icon: <FaClipboardList />, label: 'Work Orders', value: stats?.totalOrders ?? recent.length, desc: 'Assigned to your publisher account', onClick: () => navigate('/publisher/work-orders'), className: 'eduss-dashboard-card-blue' },
    { icon: <FaClipboardCheck />, label: 'Pending', value: stats?.pending ?? recent.filter(r => r.status !== 'COMPLETED').length, desc: 'Awaiting full delivery', onClick: () => navigate('/publisher/work-orders'), className: 'eduss-dashboard-card-yellow' },
    { icon: <FaTruck />, label: 'Dispatch Notes', value: stats?.issued ?? 0, desc: 'Delivery notes generated', onClick: () => navigate('/publisher/dispatch-notes'), className: 'eduss-dashboard-card-green' },
    { icon: <FaWallet />, label: 'Amount', value: new Intl.NumberFormat('en-US').format(stats?.totalAmount || 0), desc: 'Total work order value', onClick: () => navigate('/publisher/work-orders'), className: 'eduss-dashboard-card-green' },
  ], [stats, recent, navigate]);

  const charts = useMemo(() => [
    {
      title: 'Work Order Status',
      caption: 'Publisher queue',
      type: 'bar',
      data: [
        { name: 'Total', value: Number(stats?.totalOrders ?? recent.length) },
        { name: 'Pending', value: Number(stats?.pending ?? recent.filter(r => r.status !== 'COMPLETED').length) },
        { name: 'Dispatches', value: Number(stats?.issued || 0) },
      ],
      color: '#0f6fad',
    },
    {
      title: 'Recent Quantities',
      caption: 'Latest orders',
      type: 'line',
      data: recent.slice(0, 6).map(row => ({ name: row.orderNumber || `WO ${row.id}`, value: Number(row.totalQuantity || 0) })),
      color: '#10ac84',
    },
  ], [stats, recent]);

  return (
    <div className="eduss-dashboard">
      <PortalHero title="Publisher Dashboard" subtitle="Track assigned work orders, partial deliveries, and dispatch receipts." />
      <KpiGrid cards={cards} loading={!stats} />
      <DashboardChartGrid charts={charts} />
      <div className="eduss-dashboard-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Recent Work Orders</h2>
          <button className="eduss-home-cta" type="button" onClick={() => navigate('/publisher/dispatch-create')}>Create Dispatch</button>
        </div>
        <table className="mini-info-table" style={{ width: '100%' }}>
          <thead><tr><th>Order #</th><th>Date</th><th>Package</th><th>Status</th><th>Total Qty</th><th>Total Price</th></tr></thead>
          <tbody>
            {recent.map(row => (
              <tr key={row.id}>
                <td>{row.orderNumber}</td>
                <td>{row.orderDate || '-'}</td>
                <td>{row.package?.name || '-'}</td>
                <td>{row.status}</td>
                <td>{new Intl.NumberFormat('en-US').format(row.totalQuantity || 0)}</td>
                <td>{new Intl.NumberFormat('en-US').format(row.totalPrice || 0)}</td>
              </tr>
            ))}
            {stats && !recent.length && <tr><td colSpan="6">No work orders assigned yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
